import { type Vec3, vec3Sub, vec3Length, vec3Normalize } from '../../shared/utils';
import {
  isLocalPlayerInvolved,
  type LocalGateState,
} from './eventGating';

/**
 * Subset of side-effect collaborators driven by a `DamageDealt` event.
 * Mocked individually in tests; the live wiring is done in
 * {@link GameClient.wireServerEvents}.
 */
export interface DamageImpactDeps {
  attackerVisualizer: {
    showAttack: (attackerPos: Vec3, targetPos: Vec3, damage: number, missed?: boolean) => void;
  };
  effects: {
    showDamageFlash: (pos: Vec3, dir?: Vec3) => void;
    showHitSpark: (pos: Vec3, dir?: Vec3) => void;
    showCritFlash: (pos: Vec3) => void;
    showKnockbackDust: (pos: Vec3, dir: Vec3) => void;
    spawnImpactDebris: (pos: Vec3, dir: Vec3 | undefined, isCrit: boolean) => void;
    showDirectionalDamageFlash: (pos: Vec3, dir: Vec3) => void;
  };
  audio: {
    playCrit: () => void;
  } | null;
  camera: {
    addShake: (intensity: number) => void;
    addDirectionalShake: (dir: Vec3, intensity: number) => void;
    triggerHitStop: () => void;
  } | null;
  scene: { triggerDamagePost: (intensity: number) => void };
  dispatchImpactAudio: (attackerDroneId: string | undefined) => void;
  triggerHeavyHitShake: (data: Record<string, unknown>, gateState: LocalGateState) => void;
  critAttackerFeedback: { handleEvent: (event: string, data: Record<string, unknown>) => void } | null;
  getGateState: () => LocalGateState;
}

/**
 * Render the impact-side feedback for a `DamageDealt` event.
 * Returns early on miss so tracers/sparks/flashes/audio/shake stay silent —
 * HitMarkers/DamageDirection/DamageFloaters render the gray miss UI through
 * their own listeners.
 */
export function dispatchDamageImpact(
  eventType: string,
  data: Record<string, unknown>,
  deps: DamageImpactDeps,
): void {
  if (!data.position) return;
  const targetPos = data.position as Vec3;
  const ap = data.attackerPosition as Vec3 | undefined;
  const delta = ap ? vec3Sub(targetPos, ap) : undefined;
  const hitDir = delta && vec3Length(delta) > 0.01 ? vec3Normalize(delta) : undefined;
  const missed = data.missed === true;
  const isCrit = data.isCrit === true;

  if (ap && !data.projectileVisualized) {
    deps.attackerVisualizer.showAttack(ap, targetPos, (data.damage as number) ?? 0, missed);
  }

  if (missed) return;

  deps.effects.showDamageFlash(targetPos);
  deps.effects.showHitSpark(targetPos, hitDir);

  const gateState = deps.getGateState();
  const localDmg = isLocalPlayerInvolved(data, gateState);
  deps.dispatchImpactAudio(data.attackerDroneId as string | undefined);

  if (isCrit) {
    deps.audio?.playCrit();
    deps.effects.showCritFlash(targetPos);
  }
  if (localDmg) {
    deps.camera?.addShake(0.08);
    deps.scene.triggerDamagePost(0.28);
    deps.triggerHeavyHitShake(data, gateState);
  } else {
    deps.camera?.addShake(0.02);
  }

  const knockback = data.knockback as Vec3 | undefined;
  if (knockback) {
    deps.effects.showKnockbackDust(targetPos, knockback);
    const targetId = data.targetDroneId;
    const isLocalVictim = typeof targetId === 'string' && gateState.localDroneIds.has(targetId);
    if (isLocalVictim) {
      deps.camera?.addShake(0.12);
      deps.camera?.addDirectionalShake(knockback, 0.18);
    }
  }
  if (isCrit && localDmg) deps.camera?.triggerHitStop();

  deps.effects.spawnImpactDebris(targetPos, hitDir, isCrit);
  if (hitDir) deps.effects.showDirectionalDamageFlash(targetPos, hitDir);

  deps.critAttackerFeedback?.handleEvent(eventType, data);
}
